import { Component, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { MatTableDataSource } from "@angular/material/table";
import { ContentHeaderKey } from "src/app/constants/content-header";
import { TableColumnType } from "src/app/constants/table";
import { AgentCompany, AgentCarrierInfo, AgentCarrierInfoPayload } from "src/app/models/agent";
import { Broker, WorkingBroker } from "src/app/models/broker";
import { Carrier, CarrierPayload } from "src/app/models/carrier";
import { TableColumn } from "src/app/models/table";

import { AgentService } from "src/app/services/agent.service";
import { HelperService } from "src/app/services/helper.service";
import { SessionService } from "src/app/services/storage/session.service";

@Component({
    selector: 'mwp-manage-carrier-info',
    templateUrl: './carrier-info.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AgentCarrierInfoComponent implements OnInit {
    public brokers: Broker[];
    public carriers: Carrier[];
    public companies: AgentCompany[];
    public showAgentCarrierResults: boolean;

    public agentControlForm: FormGroup;
    public selectedCarrierId: number;
    public selectedCompanyId: number = 0;
    public includeDisabled: boolean;

    public agentCarrierInfoForm: FormGroup;
    public agentCarrierInfoFormArrayName: string = 'agentCarrierInfoFormArray';
    public agentCarrierMatTableData: MatTableDataSource<AgentCarrierInfo>;
    public apiUrl = ContentHeaderKey.CounselorCarrierInfo;
    public agentCarrierColumns: TableColumn[] = [
        {
            columnName: 'Name',
            propertyId: 'name',
            columnType: TableColumnType.label
        },
        {
            columnName: 'Carrier UID',
            propertyId: 'carrierUID',
            columnType: TableColumnType.input
        },
        {
            columnName: 'ID Number',
            propertyId: 'carrierIDNumber',
            columnType: TableColumnType.input
        },
        {
            columnName: 'User Defined Field 1',
            propertyId: 'udF1',
            columnType: TableColumnType.input
        },
        {
            columnName: 'User Defined Field 2',
            propertyId: 'udF2',
            columnType: TableColumnType.input
        },
    ];

    constructor(
        private agentService: AgentService,
        private formBuilder: FormBuilder,
        private sessionService: SessionService,
        private helperService: HelperService,
    ) {
    }

    ngOnInit(): void {
        this.agentControlForm = this.formBuilder.group({
            brokerId: [this.sessionService.session.selectedBrokerId, Validators.required],
            companyID: [null, Validators.required],
            carrierID: [null, Validators.required]
        });

        this.agentService.getBrokers().subscribe((response: Broker[]) => {
            this.brokers = response;
        });

        this.setBroker(this.sessionService.session.selectedBrokerId);
    }


    public brokerChange(event: number): void {
        this.setBroker(event);
    }

    public setBroker(id: number): void {
        let l_WorkingBroker = new WorkingBroker();
        l_WorkingBroker.Id = id;

        this.agentService.setWorkingBroker(l_WorkingBroker).subscribe(() => {
            this.agentControlForm.get('companyID').setValue(null);
            this.agentControlForm.get('carrierID').setValue(null);
            this.agentCarrierInfoForm = null;
            this.agentCarrierMatTableData = null;
            this.companies = null;
            this.carriers = null;
            this.selectedCarrierId = null;
            this.selectedCompanyId = 0;
            this.includeDisabled = false;

            this.agentService.getCarriers().subscribe((response: Carrier[]) => {
                this.carriers = response;
            });
        });
    }

    public carrierChange(id: number): void {
        this.selectedCarrierId = id;
        this.agentService.getEnabledCompaniesByWorkingBroker().subscribe((response: AgentCompany[]) => {
            this.companies = response;
            this.populateForm();
        });
    }

    public companyChange(id: number): void {
        this.selectedCompanyId = id;
        this.populateForm();
    }

    public populateForm(): void {
        if (this.selectedCarrierId) {
            const l_CarrierPayload = new CarrierPayload();
            l_CarrierPayload.carrierID = this.selectedCarrierId;
            l_CarrierPayload.companyID = this.selectedCompanyId;
            l_CarrierPayload.includeDisabled = this.includeDisabled;

            this.agentService.getAgentCarrierInfo(l_CarrierPayload).subscribe((response: AgentCarrierInfo[]) => {
                this.agentCarrierMatTableData = new MatTableDataSource<AgentCarrierInfo>();
                this.agentCarrierMatTableData.data = response;

                if (this.agentCarrierMatTableData.data) {
                    let l_FormArray: FormArray = new FormArray([]);
                    this.agentCarrierMatTableData.data.forEach((aci: AgentCarrierInfo) => {
                        l_FormArray.push(
                            this.formBuilder.group({
                                agentID: [aci.agentID],
                                carrierID: [aci.carrierID],
                                name: [aci.name],
                                carrierUID: [aci.carrierUID],
                                carrierIDNumber: [aci.carrierIDNumber],
                                udF1: [aci.udF1],
                                udF2: [aci.udF2]
                            })
                        );
                    });

                    this.agentCarrierInfoForm = this.formBuilder.group({
                        [this.agentCarrierInfoFormArrayName]: l_FormArray,
                    });
                } else {
                    this.agentCarrierInfoForm = null;
                    this.agentCarrierMatTableData = null;
                }

                this.showAgentCarrierResults = true;
            });
        } else {
            this.agentCarrierInfoForm = null;
            this.agentCarrierMatTableData = null;
        }
    }

    get gridAgentCarrierInfoArray(): AbstractControl<any, any>[] {
        return (<FormArray>(this.agentCarrierInfoForm.get(this.agentCarrierInfoFormArrayName))).controls;
    }

    public includeDisableChange(event: MatCheckboxChange): void {
        this.includeDisabled = event.checked;
        this.populateForm();
    }

    public save(): void {
        const l_CarrierInfoPayload = new AgentCarrierInfoPayload();
        this.gridAgentCarrierInfoArray.forEach((gacia) => {
            l_CarrierInfoPayload.carrierInfos.push(this.helperService.convertFormGroupIntoObject(gacia as FormGroup, new AgentCarrierInfo()));
        });

        this.agentService.saveAgentCarrierInfo(l_CarrierInfoPayload).subscribe();
    }
}
